import React, { ReactNode, useState, ChangeEvent, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import Tooltip from "@components/common/Tooltip";

import HPButton from "./Button";
import HPInput from "./Input";
import { InfoIcon } from "@/assets/images";

interface HPinputModalProps {
  title: string;
  children?: ReactNode;
  cancelButtonText?: string;
  confirmButtonText?: string;
  onCancel?: () => void;
  onConfirm?: (inputValue: string) => void;
  confirmButtonIcon?: ReactNode;
  headerIcon?: ReactNode;
  loading?: boolean;
  withDescription?: boolean;
  initialValue?: string;
}

const HPinputModal: React.FC<HPinputModalProps> = ({
  title,
  children,
  cancelButtonText = "Cancel",
  confirmButtonText = "Confirm",
  onCancel,
  onConfirm,
  confirmButtonIcon,
  headerIcon,
  loading = false,
  withDescription = false,
  initialValue = "",
}) => {
  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const characterLimit = 120;
  const allowedCharactersRegex = /^[a-zA-Z0-9 _.\-()]*$/;

  const trimmedValue = inputValue.trim();

  const isInputValid =
    trimmedValue.length > 0 &&
    trimmedValue.length <= characterLimit &&
    allowedCharactersRegex.test(inputValue);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isTouched) {
      setIsTouched(true);
    }
    const newValue = event.target.value.slice(0, characterLimit);
    setInputValue(newValue);
  };

  const handleConfirm = () => {
    if (isInputValid && onConfirm) {
      onConfirm(inputValue);
    }
  };

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const showInvalidMessage =
    isTouched && !allowedCharactersRegex.test(inputValue);
  const showErrorMessage = isTouched && !isInputValid && inputValue.length > 0;

  return (
    <div
      className={`bg-white rounded-lg  shadow-lg flex flex-col max-w-fit w-auto mx-auto min-w-[540px]`}
    >
      <div className="p-6 flex gap-1">
        {headerIcon} <h2 className="text-lg font-semibold">{title}</h2>
      </div>

      <div className="text-sm border-t border-b border-hp-border-default">
        <div className="p-6 flex flex-col gap-2">
          <Box className="flex flex-col gap-1">
            <HPInput
              value={inputValue}
              onChange={handleChange}
              error={showErrorMessage}
              label="Dataset name*"
            />
            <Box className="flex justify-between items-center">
              <Typography
                variant="caption"
                sx={{
                  color: "var(--color-hp-error)",
                  visibility: showInvalidMessage ? "visible" : "hidden",
                  height: "20px",
                }}
              >
                Name contains invalid characters
              </Typography>
              <Typography variant="body2" className="text-sm">
                {inputValue.length}/{characterLimit}
              </Typography>
            </Box>
          </Box>
          {withDescription && (
            <div className="flex items-center gap-1">
              <InfoIcon />
              <p className="text-hp-day-secondary text-sm">
                You can add more surveys later
              </p>
            </div>
          )}

          <div>{children}</div>
        </div>
      </div>

      <div className="flex justify-end gap-1 p-4">
        <HPButton
          ariaLabel="Cancel"
          variant="text"
          size="medium"
          color="primary"
          onClick={onCancel}
          className="text-hp-text-day-primary"
        >
          {cancelButtonText}
        </HPButton>
        <Tooltip
          title={inputValue.length === 0 && "Enter dataset name"}
          placement="top"
          arrow
        >
          <span>
            <HPButton
              disabled={loading || !isInputValid}
              startIcon={confirmButtonIcon}
              ariaLabel="Confirm"
              variant="contained"
              size="medium"
              onClick={handleConfirm}
            >
              {confirmButtonText}
            </HPButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default HPinputModal;
